// Cloud-relay endpoint resolution for the UI shell.
//
// ONE relay-authoritative architecture, two deployments by CONFIG:
//   - cloud       → our endpoints are baked in as the DEFAULT (nothing to set).
//   - self-hosted → the customer points the UI at THEIR relay by building with
//                    VITE_RELAY_URL set (an http(s):// base or a ws(s):// url).
//
// Historically the cloud relay was hardcoded in three places (state.svelte.ts,
// login/+page.svelte, invite/[token]/+page.svelte). They now all read from here
// so a self-host build overrides the endpoint in ONE spot and cloud is unchanged.

const DEFAULT_CLOUD_RELAY = "https://relay.cyborg7.com";

// Normalize any relay reference (http(s):// base OR ws(s):// url, with or without
// a trailing /api/ws or /ws path) down to a bare https:// origin.
function normalizeToHttpsBase(raw: string): string {
  return raw
    .trim()
    .replace(/^ws:/i, "http:")
    .replace(/^wss:/i, "https:")
    .replace(/\/api\/ws\/?$/i, "")
    .replace(/\/ws\/?$/i, "")
    .replace(/\/+$/, "");
}

// Build/runtime override for self-hosted deployments. Unset ⇒ the cloud default,
// so a stock cloud build behaves exactly as before.
const RELAY_OVERRIDE = (import.meta.env.VITE_RELAY_URL as string | undefined)?.trim();

/** HTTP(S) origin of the relay (e.g. https://relay.cyborg7.com). No trailing slash. */
export const CLOUD_RELAY_HTTP_BASE: string = normalizeToHttpsBase(
  RELAY_OVERRIDE || DEFAULT_CLOUD_RELAY,
);

/** Origins whose `/api/assets/o/<key>` urls are ours: this build's relay and a self-host's own origin. */
export function trustedAssetOrigins(): string[] {
  const origins = [CLOUD_RELAY_HTTP_BASE];
  if (typeof location !== "undefined" && location.origin && location.origin !== "null") {
    origins.push(location.origin);
  }
  return origins;
}

/** Guest WebSocket URL of the relay (e.g. wss://relay.cyborg7.com/api/ws). */
export const CLOUD_RELAY_WS_URL: string = `${CLOUD_RELAY_HTTP_BASE.replace(
  /^http:/i,
  "ws:",
).replace(/^https:/i, "wss:")}/api/ws`;
